import { clsx } from 'clsx';
import { cubicBezier } from 'motion';
import type { HTMLMotionProps } from 'motion/react';
import { motion } from 'motion/react';
import { forwardRef } from 'react';
import type { PropsWithChildren, PropsWithoutRef } from 'react';
import { foundations } from '../../styles/foundations.css';
import { ellipsis } from '../../styles/utilities.css';
import { Flex } from '../Flex/Flex';
import { Spinner } from '../Spinner/Spinner';
import * as styles from './Button.css';
import type { ButtonVariants } from './Button.css';

const cubicBezierTransition = cubicBezier(0.14, 0.38, 0.41, 0.99);
export const buttonLayoutTransition = { duration: 0.2, ease: cubicBezierTransition };

export type ButtonProps = PropsWithoutRef<
  ButtonVariants &
    HTMLMotionProps<'button'> &
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
      readonly loading?: boolean;
      readonly layout?: boolean;
      readonly ellipsis?: boolean;
    }
>;

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      children,
      variant = 'secondary',
      size = 'lg',
      disabled,
      loading,
      className: externalClassName,
      layout = true,
      ellipsis: applyEllipsis = true,
      ...props
    },
    ref,
  ) => {
    const foundationClass = foundations({
      borderRadius: 'sm',
      glow: variant === 'primary' ? 'sm' : undefined,
      shadow: variant === 'secondary' ? 'sm' : undefined,
    });

    return (
      <Flex
        alignItems="center"
        as={motion.button}
        className={clsx(foundationClass, styles.button({ variant, size, disabled, loading }), externalClassName)}
        disabled={disabled}
        justifyContent="center"
        layout={layout}
        ref={ref}
        transition={buttonLayoutTransition}
        type="button"
        {...props}
      >
        {loading ? (
          <>
            <Spinner className={styles.buttonSpinner} />
            <span className={styles.buttonLoadingText}>{children}</span>
          </>
        ) : (
          <span className={clsx(styles.buttonChildren, { [ellipsis]: applyEllipsis })}>{children}</span>
        )}
      </Flex>
    );
  },
);
